import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import useDeepCompareEffect from 'use-deep-compare-effect';
import moment from 'moment-timezone';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import {DataGridPro} from '@mui/x-data-grid-pro';
import {faPlus} from '@fortawesome/pro-regular-svg-icons';

import StudentForm from './StudentForm';
import {BluePillButton} from '../../common/components/BluePillButton';
import DataGridQuickSearchToolbar from './DataGridQuickSearchToolbar';
import {fetchStudents, selectStudents, selectIsLoading as selectIsBuilderLoading} from '../../redux/slices/builder';
import {selectUserGroups, selectUsers, selectIsLoading as selectIsUserLoading} from '../../redux/slices/user';

import baseStyles from '../../css/Base.module.css';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export default function SelectStudentDialog({
  isOpen,
  handleClose,
  onSelectStudent,
  onStudentCreate,
  excludeStudents = [],
}) {
  const dispatch = useDispatch();

  const [mode, setMode] = useState('SELECT'); // SELECT, CREATE
  const [studentsClone, setStudentsClone] = useState([]); // TODO: Refactor this away
  const [searchText, setSearchText] = useState('');
  const [dataGridToolbarFilterButtonEl, setDataGridToolbarFilterButtonEl] = useState(false);

  const students = useSelector(selectStudents);
  const excludeStudentIDs = excludeStudents.map((student) => student.id);
  const nonExcludedStudents = students.filter((student) => !excludeStudentIDs.includes(student.id));
  const userGroups = useSelector(selectUserGroups);
  const users = useSelector(selectUsers);
  const isBuilderLoading = useSelector(selectIsBuilderLoading);
  const isUserLoading = useSelector(selectIsUserLoading);

  const isLoading = isBuilderLoading || isUserLoading;

  // Quick search toolbar BEGIN
  function escapeRegExp(value) {
    return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  }

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
    const filteredRows = nonExcludedStudents.filter((row) => {
      return Object.keys(row).some((field) => {
        let searchValue;
        if (field === 'tags') searchValue = row[field].map((tag) => tag.name).join(',');
        else searchValue = row[field]?.toString() ?? '';

        return searchRegex.test(searchValue);
      });
    });
    setStudentsClone(filteredRows || []);
  };
  // Quick search toolbar END

  useEffect(() => {
    setStudentsClone([]);
    if (isOpen) dispatch(fetchStudents());
    else setMode('SELECT');
  }, [dispatch, isOpen]);

  useDeepCompareEffect(() => {
    setStudentsClone(nonExcludedStudents);
  }, [students, users, userGroups, isBuilderLoading, excludeStudents]);

  function onStudentSelect(student) {
    onSelectStudent(student);
    handleClose();
    setStudentsClone([]);
  }

  function handleStudentCreate(student) {
    onStudentCreate(student);
    handleClose();
    setStudentsClone([]);
  }

  function renderNewStudent() {
    return <StudentForm postSave={handleStudentCreate} onCancel={handleClose} />;
  }

  function renderExistingStudent() {
    return (
      <>
        {isLoading && (
          <div className={baseStyles['progress-container']}>
            <CircularProgress />
          </div>
        )}

        {!isLoading && nonExcludedStudents?.length >= 0 && (
          <div style={{height: 'calc(100vh - 117px)', width: '100%'}}>
            <div style={{display: 'flex', height: '100%'}}>
              <div style={{flexGrow: 1}}>
                <DataGridPro
                  pagination
                  components={{Toolbar: DataGridQuickSearchToolbar}}
                  componentsProps={{
                    toolbar: {
                      value: searchText,
                      onChange: (event) => requestSearch(event.target.value),
                      clearSearch: () => requestSearch(''),
                      setFilterButtonEl: setDataGridToolbarFilterButtonEl,
                    },
                    panel: {
                      anchorEl: dataGridToolbarFilterButtonEl,
                    },
                  }}
                  rows={studentsClone}
                  columns={[
                    {
                      field: 'action',
                      type: 'actions',
                      hideable: false,
                      getActions: (params) => [
                        <BluePillButton
                          onClick={(event) => onStudentSelect(params.row)}
                          size="small"
                          sx={{paddingLeft: '15px', paddingRight: '15px'}}
                        >
                          Select
                        </BluePillButton>,
                      ],
                    },
                    {
                      field: 'name',
                      headerName: 'Name',
                      width: 250,
                      valueGetter: (params) => `${params.row.lastName}, ${params.row.firstName}`,
                    },
                    {
                      field: 'birthDate',
                      headerName: 'Birthdate',
                      width: 150,
                      type: 'date',
                      valueFormatter: (params) => moment(params.value).format('M/D/YYYY'),
                    },
                    {field: 'authCode', headerName: 'Join Code', width: 250},
                    {
                      field: 'tags',
                      headerName: 'Tags',
                      width: 300,
                      valueGetter: (params) => params.row.tags.map((tag) => tag.name).join(','),
                      renderCell: (params) => {
                        const {tags} = params.row;
                        return (
                          <Stack direction="row" spacing={1} sx={{overflowX: 'auto'}}>
                            {tags.map((tag) => (
                              <Chip key={tag._id} label={tag.name} />
                            ))}
                          </Stack>
                        );
                      },
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <Dialog fullScreen open={isOpen} onClose={handleClose} TransitionComponent={Transition}>
      <AppBar sx={{position: 'relative'}} className="bg-dark-blue">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
          <Typography sx={{ml: 2, flex: 1}} variant="h6" component="div">
            {`${mode === 'SELECT' ? 'Select' : 'Add'} Student`}
          </Typography>
          {mode === 'SELECT' && (
            <IconButton
              onClick={() => setMode('CREATE')}
              className="bg-success"
              size="large"
              sx={{'&:hover': {backgroundColor: 'rgba(66, 184, 94, 0.8)'}}}
            >
              <FontAwesomeIcon icon={faPlus} size="xs" className="clr-gray-200" />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {mode === 'SELECT' ? renderExistingStudent() : renderNewStudent()}
    </Dialog>
  );
}
